import type { PageResponse } from '../types/api';
import type {
  DocumentResponse,
  PresignUploadRequest,
  PresignedUploadResponse,
} from '../types/document';
import { api } from './api';

export async function presignUpload(
  body: PresignUploadRequest,
): Promise<PresignedUploadResponse> {
  const { data } = await api.post<PresignedUploadResponse>(
    '/documents/presign-upload',
    body,
  );
  return data;
}

export async function downloadDocument(documentId: string): Promise<void> {
  const response = await api.get(`/documents/${documentId}/download`, {
    responseType: 'blob',
  });
  
  const contentDisposition = response.headers['content-disposition'];
  let fileName = 'download';
  if (contentDisposition) {
    const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (fileNameMatch && fileNameMatch[1]) {
      fileName = fileNameMatch[1].replace(/['"]/g, '');
    }
  }
  
  const contentType = response.headers['content-type'] || 'application/octet-stream';
  const blob = new Blob([response.data], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function viewDocument(documentId: string): Promise<string> {
  const response = await api.get(`/documents/${documentId}/view`, {
    responseType: 'blob',
  });
  
  const contentType = response.headers['content-type'] || 'application/octet-stream';
  const blob = new Blob([response.data], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  
  return url;
}

export async function deleteDocument(documentId: string): Promise<void> {
  await api.delete(`/documents/${documentId}`);
}

export async function getDocumentsByTaxCase(
  taxCaseId: string,
  page = 0,
  size = 20,
): Promise<PageResponse<DocumentResponse>> {
  const { data } = await api.get<PageResponse<DocumentResponse>>(
    `/documents/tax-case/${taxCaseId}`,
    { params: { page, size } },
  );
  return data;
}

export async function uploadFileToPresignedUrl(
  uploadUrl: string,
  file: File,
  contentType: string,
): Promise<void> {
  try {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType,
      },
      body: file,
    });

    if (!response.ok) {
      let errorText = 'Unknown error';
      try {
        errorText = await response.text();
      } catch {
        errorText = `HTTP ${response.status}: ${response.statusText}`;
      }
      throw new Error(`Upload failed with status ${response.status}: ${errorText}`);
    }
  } catch (err) {
    if (err instanceof TypeError) {
      // Network-level error (typically CORS or connectivity).
      // With presigned S3 PUTs the file is often stored successfully but
      // the browser cannot read the response due to CORS headers.
      // The document record already exists in the DB, so we treat this
      // as a success and let the caller refetch the document list.
      console.warn('S3 upload returned a network error (likely CORS). Assuming upload succeeded.', err);
      return;
    }
    throw err;
  }
}
